/**
 * Navegación dentro de una misma página
 * -----------------------------------------------------------------------------
 * Muchas pantallas no son una sola vista: el listado sin datos, el formulario de
 * creación, el de edición… viven en la misma URL y se turnan. El enlace «Volver»
 * de la cabecera tiene que llevar al paso anterior de ESA secuencia, no siempre
 * al mismo sitio, y desaparecer cuando ya no hay nada detrás.
 *
 *   const nav = CompanyTheme.pageNav('.page-back');
 *
 *   nav.base(() => mostrarListado());          // el estado de partida
 *   document.querySelector('#crear').onclick = () => nav.push('crear', () => abrirFormulario());
 *
 * Se apoya en `history.pushState`, así que el botón atrás del navegador (y el
 * gesto de volver en el móvil) hacen lo mismo que el enlace: nadie sale de la
 * pantalla sin querer por haber abierto un formulario.
 *
 * El theme no decide QUÉ se pinta en cada paso: solo lleva la cuenta y llama a
 * la función que le dio la aplicación.
 */

// Marca propia dentro de `history.state`, para no confundir nuestras entradas
// con las de un router de la aplicación.
const KEY = 'companyPageNav';

class PageNav {
  constructor(link, options = {}) {
    this.link = typeof link === 'string' ? document.querySelector(link) : link;

    // En el estado de partida no hay nada detrás dentro de la pantalla, así que
    // el enlace se esconde: un «Volver» siempre visible no dice a dónde lleva.
    // `showAtBase` es para las pantallas que sí cuelgan de otra página, donde el
    // enlace tiene un `href` de verdad al que salir.
    this.showAtBase = options.showAtBase === true;

    this.states = [];
    this.depth = 0;
    this.onChange = options.onChange || null;

    if (this.link) {
      this.link.addEventListener('click', (event) => {
        if (this.depth === 0) return; // que el enlace haga lo suyo y salga de la página
        event.preventDefault();
        this.back();
      });
    }

    window.addEventListener('popstate', (event) => {
      const target = event.state && typeof event.state[KEY] === 'number' ? event.state[KEY] : 0;

      // Si se volvió más atrás de nuestro punto de partida, el navegador ya está
      // saliendo de la pantalla: no hay nada que pintar.
      if (target > this.states.length - 1) return;

      this._go(target);
    });
  }

  /**
   * Estado de partida. Se pinta enseguida y es al que se llega volviendo del
   * todo. Reinicia la secuencia, así que sirve también para «cancelar».
   */
  base(render) {
    this.states = [render];
    this.depth = 0;

    // Al recargar, la entrada del historial conserva su `state`. Si se recarga
    // con un paso abierto, esa marca sobreviviría y el punto de partida se
    // creería un paso avanzado. Se borra la nuestra y se respeta el resto, que
    // puede ser del enrutado de la aplicación.
    const current = window.history.state;

    if (current && typeof current[KEY] === 'number') {
      const clean = { ...current };
      delete clean[KEY];
      window.history.replaceState(clean, '', window.location.href);
    }

    this._render();
    return this;
  }

  /**
   * Avanza un paso. Añade una entrada al historial, de forma que el enlace y el
   * botón atrás del navegador regresen al paso actual.
   *
   * @param {string} name Nombre del paso, solo informativo (eventos, depuración).
   * @param {Function} render Qué pintar al llegar —y al volver a él más tarde—.
   */
  push(name, render) {
    if (!this.states.length) this.states = [() => {}];

    this.states = this.states.slice(0, this.depth + 1);
    this.states.push(render);
    this.depth = this.states.length - 1;

    // Sin cambiar la URL: la pantalla sigue siendo la misma y así no se pisa el
    // enrutado de la aplicación.
    window.history.pushState({ [KEY]: this.depth, name }, '', window.location.href);

    this._render();
    return this;
  }

  /** Un paso atrás. Delega en el historial para que todo quede sincronizado. */
  back() {
    if (this.depth === 0) return this;
    window.history.back();
    return this;
  }

  /** Vuelve al estado de partida, tantos pasos como haga falta. */
  reset() {
    if (this.depth > 0) window.history.go(-this.depth);
    return this;
  }

  _go(depth) {
    this.depth = depth;
    this._render();
  }

  _render() {
    const render = this.states[this.depth];
    if (typeof render === 'function') render();

    if (this.link) {
      this.link.hidden = this.depth === 0 && !this.showAtBase;
    }

    if (this.onChange) this.onChange({ depth: this.depth, total: this.states.length });
  }
}

/**
 * @param {string|Element} [link='.page-back'] Enlace «Volver» de la cabecera.
 * @param {Object} [options] `onChange({depth, total})`.
 */
export function pageNav(link = '.page-back', options = {}) {
  return new PageNav(link, options);
}

export default pageNav;
